import { useSmsAutomationAccess } from '@features/sms/useSmsAutomationAccess';
import { SmsHistory } from '@features/sms/components/SmsHistory';
import AccordionSection from '@features/profile/components/AccordionSection';
import { Button, Divider, Paper, Stack, Tooltip } from '@mui/material';
import { useParams } from 'react-router-dom';
import QrCodeIcon from '@mui/icons-material/QrCode';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import AppTheme from '@libs/mui/AppTheme';
import ClientProfileGrid from '../features/client/components/ClientProfileGrid';
import { useSuspenseClientQuery, useDeleteClientMutation } from '../features/client/queries';
import VisitDetailCard from '../features/visits/components/visit-detail/components/visit-detail-card';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { ClientActivationQRDialog } from '../features/client/components/ClientActivationQRDialog';
import { useRef, useState } from 'react';
import { ImperativeDialog } from '@shared/components/dialogs/imperative-dialog';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import { useSubscriptionPlan } from '@shared/store';
import { getDateTimeFromUtcToLocal, isSubscriptionExpert } from '@shared/entity';
import { useRevokeReservationAccessMutation } from '../features/customer-reservations/queries';
import { useFormatCurrency } from '@features/currency/currency.hooks';

const ClientProfile = () => {
  const smsAutomationEnabled = useSmsAutomationAccess();
  const { clientId } = useParams();
  const { mutate } = useDeleteClientMutation();
  const { mutate: revokeAccess } = useRevokeReservationAccessMutation();
  const { data: clientData } = useSuspenseClientQuery(clientId!);
  const { t } = useIntlTranslations();
  const deleteDialogRef = useRef<ImperativeFormDialogRefType | null>(null);
  const revokeDialogRef = useRef<ImperativeFormDialogRefType | null>(null);
  const plan = useSubscriptionPlan();
  const { format: fmtCurrency } = useFormatCurrency();

  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  return (
    <>
      <Stack spacing={2}>
        <ClientProfileGrid clientData={clientData} />
        <Divider />
        <Stack spacing={2} direction="row" alignItems="center" justifyContent="center">
          {isSubscriptionExpert(plan) && clientData.hasActiveSessions ? (
            <Paper
              variant="outlined"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: 3,
                borderColor: 'secondary.light',
                backgroundColor: '#87f5c1',
                alignItems: 'center',
              }}
            >
              <Button
                onClick={() => setQrDialogOpen(true)}
                size="medium"
                sx={{
                  backgroundColor: `${AppTheme.palette.common.white}`,
                  color: `${AppTheme.palette.primary.main}`,
                }}
                startIcon={<QrCodeIcon fontSize="small" color="secondary" />}
              >
                {t('clientProfilePage.qr')}
              </Button>
              <Tooltip title={t('clientProfilePage.revokeReservationAccess')}>
                <Button
                  onClick={() => revokeDialogRef.current?.open()}
                  size="medium"
                  sx={{
                    backgroundColor: `${AppTheme.palette.common.white}`,
                    color: `${AppTheme.palette.primary.main}`,
                  }}
                  startIcon={<NoAccountsIcon fontSize="small" color="warning" />}
                >
                  {t('clientProfilePage.disableReservations')}
                </Button>
              </Tooltip>
            </Paper>
          ) : (
            isSubscriptionExpert(plan) && (
              <Button
                onClick={() => setQrDialogOpen(true)}
                size="medium"
                sx={{
                  backgroundColor: `${AppTheme.palette.common.white}`,
                  color: `${AppTheme.palette.primary.main}`,
                }}
                startIcon={<QrCodeIcon fontSize="small" color="secondary" />}
              >
                {t('clientProfilePage.qr')}
              </Button>
            )
          )}
          <Tooltip title={t('clientProfilePage.deleteClientContrainet')}>
            <Button
              onClick={() => deleteDialogRef.current?.open()}
              size="medium"
              sx={{
                backgroundColor: `${AppTheme.palette.common.white}`,
                color: `${AppTheme.palette.primary.main}`,
              }}
              startIcon={<DeleteForeverIcon fontSize="small" color="error" />}
            >
              {t('clientProfilePage.deleteClient')}
            </Button>
          </Tooltip>
        </Stack>
        <Divider />
        {smsAutomationEnabled && (
          <AccordionSection id="client-sms-history" title={t('clientProfilePage.smsCommunication')}>
            <SmsHistory clientId={clientData.id} />
          </AccordionSection>
        )}
        {clientData.visits.map((visit, index) => {
          const visitCount = clientData.visits.length;
          return (
            <VisitDetailCard
              autor={visit.autor}
              visitId={visit.id}
              date={getDateTimeFromUtcToLocal(visit.date)}
              index={visitCount - index}
              clientId={clientData.id}
              key={index}
              serviceName={
                visit.visitServices &&
                visit.visitServices.map(s => s.service.serviceName).join(', ')
              }
              paidPriceService={fmtCurrency(
                Number(visit.paidPrice) - Number(visit.loyaltyPointsUsed),
              )}
              paidPriceGoods={fmtCurrency(Number(visit.totalSalesRevenue))}
            />
          );
        })}
        {isSubscriptionExpert(plan) && (
          <ClientActivationQRDialog
            open={qrDialogOpen}
            onClose={() => setQrDialogOpen(false)}
            clientId={clientData.id}
            clientName={[clientData.firstName, clientData.lastName].filter(Boolean).join(' ')}
            clientPhone={clientData.phone}
            clientPhonePrefix={clientData.phonePrefix}
            hasAllowedServices={!!clientData.allowedServices?.length}
          />
        )}
      </Stack>
      <ImperativeDialog
        ref={deleteDialogRef}
        title={t('clientProfilePage.deleteClientConfirmTitle')}
        dialogHelperText={t('clientProfilePage.deleteClientConfirmHelperTitle')}
        onConfirm={() => clientId && mutate(clientId)}
      />
      <ImperativeDialog
        ref={revokeDialogRef}
        title={t('clientProfilePage.revokeReservationAccess')}
        dialogHelperText={t('clientProfilePage.revokeReservationAccessHelper')}
        onConfirm={() => clientId && revokeAccess(clientId)}
      />
    </>
  );
};
export default ClientProfile;
